import React from 'react';
import { faChevronRight, faEllipsis } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { Slot } from '@radix-ui/react-slot';

import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

function Breadcrumb({ ...props }: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        'flex flex-wrap items-center gap-1.5 text-sm wrap-break-word sm:gap-2.5',
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn('inline-flex items-center gap-1.5', className)}
      {...props}
    />
  );
}

function BreadcrumbLink({
  asChild,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
}) {
  const isInteractive = asChild || props.href !== undefined;
  const Comp = asChild ? Slot : isInteractive ? 'a' : 'span';

  return (
    <Comp
      data-slot="breadcrumb-link"
      className={cn(
        'opacity-80',
        isInteractive ? 'hover:opacity-100 transition-opacity' : 'cursor-default',
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      aria-current="page"
      className={cn('font-normal', className)}
      {...props}
    />
  );
}

function BreadcrumbSeparator({ children, className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('[&>svg]:size-3.5', className)}
      {...props}
    >
      {children ?? <Icon icon={faChevronRight} />}
    </li>
  );
}

function BreadcrumbEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <Icon icon={faEllipsis} />
      <span className="sr-only">More</span>
    </span>
  );
}

type BreadcrumbProps = {
  label: string;
  href?: string;
};

interface BreadcrumbsComponentProps extends React.ComponentProps<'nav'> {
  breadcrumbs: BreadcrumbProps[];
  linkClassName?: string;
  pageClassName?: string;
  showAllOnMobile?: boolean;
}

function BreadcrumbsComponent({
  breadcrumbs,
  linkClassName,
  pageClassName,
  showAllOnMobile,
  ...props
}: BreadcrumbsComponentProps) {
  return (
    <Breadcrumb {...props}>
      <BreadcrumbList>
        {breadcrumbs.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const hideOnMobile = !isLast && !showAllOnMobile;
          return (
            <React.Fragment key={breadcrumb.href ?? breadcrumb.label ?? index}>
              <BreadcrumbItem className={hideOnMobile ? 'hidden md:block' : undefined}>
                {isLast ? (
                  <BreadcrumbPage className={cn('line-clamp-1 overflow-hidden', pageClassName)}>
                    {breadcrumb.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink
                    href={breadcrumb.href}
                    className={cn('text-muted-foreground', linkClassName)}
                  >
                    {breadcrumb.label}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && (
                <BreadcrumbSeparator className={hideOnMobile ? 'hidden md:block' : undefined} />
              )}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbsComponent,
  BreadcrumbSeparator,
};
